/**
 * @lodex-pro/web-shared/api/api-error — structured error thrown by useApiQuery.
 *
 * The lodeX Pro API returns a typed error body on every non-2xx response:
 *   { error: { code: string, message: string }, requestId: string }
 *
 * Before this class, `useApiQuery` threw a bare `Error("API <status>: <path>")`
 * and discarded the body — so a component could only tell WHAT failed by
 * substring-matching the status out of the message string (e.g.
 * `error.message.includes('409')`). That is brittle: a request id or row count
 * containing the digits flips it, and it couples UI logic to a human-readable
 * string. `ApiError` carries the status and the body's `code` as fields so
 * callers can branch on `error.code === 'SOME_CODE'` structurally.
 *
 * `message` is kept BYTE-IDENTICAL to the old format (`API <status>: <path>`)
 * so any existing consumer that still reads `error.message` keeps working
 * unchanged — this class is purely additive.
 *
 * OPEN-199: `message` is for code, never for people. Screens that showed it put
 * "API 409: /v1/platform/team/…/resend-invite" in a toast. `userMessage` is the
 * body's `error.message`, the catalog's plain sentence, and `apiErrorText(err,
 * fallback)` is what a screen shows.
 */

/** One `error.fields[]` entry of an API error body. */
export interface ApiErrorField {
  readonly path: string;
  readonly reason: string;
}

export class ApiError extends Error {
  /** HTTP status of the failed response. */
  readonly status: number;
  /**
   * The `error.code` from the API error body (e.g. 'OPERATOR_PORTAL_NOT_LINKED'),
   * or null when the body was absent / not the expected JSON shape (e.g. a
   * non-JSON proxy/gateway error).
   */
  readonly code: string | null;
  /**
   * The body's `error.fields` (empty when absent). A few 409s name what they collide with
   * here, e.g. 0205's `ticket_prefix_taken_by: <customer>`.
   */
  readonly fields: readonly ApiErrorField[];
  /**
   * The body's `error.message`: the error catalog's plain-words sentence for `code`
   * (OPEN-199), or null when the body had none.
   */
  readonly userMessage: string | null;

  constructor(
    status: number,
    code: string | null,
    message: string,
    fields: readonly ApiErrorField[] = [],
    userMessage: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.userMessage = userMessage;
    // Restore the prototype chain — required for `instanceof ApiError` to work
    // after TypeScript downlevels `extends Error` (a well-known TS/ES caveat).
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Extract the `error.code` string from a parsed API error body, if present.
 * Returns null for any shape that isn't `{ error: { code: string } }`.
 */
export function extractApiErrorCode(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const { error: err } = body;
  if (typeof err !== 'object' || err === null || !('code' in err)) return null;
  const { code } = err;
  return typeof code === 'string' ? code : null;
}

/**
 * Extract the `error.message` string (the catalog's plain sentence) from a parsed API error
 * body; null for any other shape or an empty string.
 */
export function extractApiErrorMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null || !('error' in body)) return null;
  const { error: err } = body;
  if (typeof err !== 'object' || err === null || !('message' in err)) return null;
  const { message } = err;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

/** "API 409: /v1/..." / "API 503": the old status-and-path text, never shown to anyone. */
const RAW_API_TEXT = /^API \d{3}\b/;

/**
 * OPEN-199 — the words a screen shows for a failed call: the API's own plain sentence for an
 * ApiError, else `fallback`. A plain Error keeps its message (a check the screen made itself),
 * unless it is the old "API <status>: <path>" text or a network TypeError ("Failed to fetch").
 */
export function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.userMessage ?? fallback;
  if (err instanceof TypeError) return fallback;
  if (err instanceof Error && err.message.trim() !== '' && !RAW_API_TEXT.test(err.message)) {
    return err.message;
  }
  return fallback;
}

/**
 * Extract `error.fields` from a parsed API error body; entries that are not
 * `{ path: string, reason: string }` are dropped. Returns [] for any other shape.
 */
export function extractApiErrorFields(body: unknown): ApiErrorField[] {
  if (typeof body !== 'object' || body === null || !('error' in body)) return [];
  const { error: err } = body;
  if (typeof err !== 'object' || err === null || !('fields' in err)) return [];
  const { fields } = err;
  if (!Array.isArray(fields)) return [];
  return fields.flatMap((f: unknown): ApiErrorField[] => {
    if (typeof f !== 'object' || f === null) return [];
    const { path, reason } = f as { path?: unknown; reason?: unknown };
    return typeof path === 'string' && typeof reason === 'string' ? [{ path, reason }] : [];
  });
}

/**
 * The text after `<prefix>: ` in the first field reason that starts with it, on an ApiError;
 * null otherwise. `apiErrorReasonValue(err, 'ticket_prefix_taken_by')` -> the customer name.
 */
export function apiErrorReasonValue(err: unknown, prefix: string): string | null {
  if (!(err instanceof ApiError)) return null;
  const head = `${prefix}: `;
  const hit = err.fields.find((f) => f.reason.startsWith(head));
  return hit === undefined ? null : hit.reason.slice(head.length);
}

/**
 * The ApiError for a failed response: its code, fields and plain message from the body (any
 * shape tolerated, a non-JSON proxy error included), and the old "API <status>: <label>" text as
 * `message`. Every hand-written fetch throws this, so no screen has to parse a body (OPEN-199).
 */
export async function apiErrorFromResponse(res: Response, label: string): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return new ApiError(
    res.status,
    extractApiErrorCode(body),
    `API ${res.status.toString()}: ${label}`,
    extractApiErrorFields(body),
    extractApiErrorMessage(body),
  );
}
